import React, { ReactNode, useEffect, useState } from 'react';
import { DiJira, DiTrello } from 'react-icons/di';
import { FaGithub, FaSlack, FaTelegram } from 'react-icons/fa';
import { LuBrainCircuit, LuMail, LuServer } from 'react-icons/lu';
import { SiAnthropic, SiGooglegemini, SiOpenai } from 'react-icons/si';

import { AiCredential } from '../entities/ai-credential';
import { CodeAgentProvider } from '../entities/code-agent-task';
import { listAiCredentials } from '../shared/api/ai-credentials';
import { disconnectIntegration, fetchIntegrationUrls, fetchIntegrations } from '../shared/api/integration';
import type { IntegrationUrls } from '../shared/api/integration';
import {
  disconnectNotificationChannel,
  fetchLinkedAccounts,
  LinkedAccount,
  NotificationChannel,
} from '../shared/api/notifications';
import AiProviderIntegration from '../widgets/integrations/AiProviderIntegration';
import EmailIntegration from '../widgets/integrations/EmailIntegration';
import GithubIntegration from '../widgets/integrations/GithubIntegration';
import IntegrationCard from '../widgets/integrations/IntegrationCard';
import IntegrationTile from '../widgets/integrations/IntegrationTile';
import JiraIntegration from '../widgets/integrations/JiraIntegration';
import McpIntegration from '../widgets/integrations/McpIntegration';
import SlackIntegration from '../widgets/integrations/SlackIntegration';
import TelegramIntegration from '../widgets/integrations/TelegramIntegration';
import TrelloIntegration from '../widgets/integrations/TrelloIntegration';
import { Modal } from '../widgets/ui/Modal';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs font-semibold opacity-50 uppercase tracking-wide px-1">{children}</div>
);

// One entry per tile — everything the grid needs to render the compact card, plus everything the
// modal needs to render the full detail/connect view when that card is clicked.
interface IntegrationEntry {
  id: string;
  icon: ReactNode;
  iconClassName?: string;
  title: string;
  description: string;
  connected: boolean;
  highlight?: boolean;
  badge?: ReactNode;
  status?: ReactNode; // extra header content in the modal only (e.g. a Refresh button)
  content: ReactNode;
}

// ~10 integrations now (trackers, AI keys, notifications, MCP) — too many to keep fully expanded
// inline on one page (that's how this page used to render, one full connect widget per row). Now
// a compact tile grid; clicking a tile opens its full connect/manage UI in a Modal instead.
const IntegrationPage: React.FC = () => {
  const [isJiraConnected, setIsJiraConnected] = useState<boolean>(false);
  const [isTrelloConnected, setIsTrelloConnected] = useState<boolean>(false);
  const [integrationUrls, setIntegrationUrls] = useState<IntegrationUrls>({
    jiraUrl: null,
    trelloUrl: null,
  });
  const [isGithubConnected, setIsGithubConnected] = useState<boolean>(false);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [credentials, setCredentials] = useState<AiCredential[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const telegramAccount = linkedAccounts.find((a) => a.channel === 'telegram');
  const slackAccount = linkedAccounts.find((a) => a.channel === 'slack');
  const emailAccount = linkedAccounts.find((a) => a.channel === 'email');
  const credentialFor = (provider: CodeAgentProvider) => credentials.find((c) => c.provider === provider);

  const loadLinkedAccounts = async () => {
    try {
      const accounts = await fetchLinkedAccounts();
      setLinkedAccounts(accounts);
    } catch {
      // silent — not critical
    }
  };

  const loadCredentials = async () => {
    try {
      setCredentials(await listAiCredentials());
    } catch {
      // silent — not critical
    }
  };

  const initData = () => {
    setError(null);
    Promise.all([
      fetchIntegrations().then((integrations) => {
        setIsJiraConnected(!!integrations?.isJiraConnected);
        setIsTrelloConnected(!!integrations?.isTrelloConnected);
        setIsGithubConnected(!!integrations?.isGitConnected);
      }),
      fetchIntegrationUrls().then(setIntegrationUrls),
      loadLinkedAccounts(),
      loadCredentials(),
    ])
      .catch(() => setError('Failed to load integrations.'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    initData();
  }, []);

  const onDisconnect = async (integrationType: string) => {
    try {
      setError(null);
      await disconnectIntegration(integrationType);
      if (integrationType === 'jira') {
        setIsJiraConnected(false);
        setIntegrationUrls((prev) => ({ ...prev, jiraUrl: null }));
      }
      if (integrationType === 'trello') {
        setIsTrelloConnected(false);
        setIntegrationUrls((prev) => ({ ...prev, trelloUrl: null }));
      }
      if (integrationType === 'github') setIsGithubConnected(false);
    } catch {
      setError('Failed to disconnect the integration.');
    }
  };

  const onDisconnectNotification = async (channel: NotificationChannel) => {
    await disconnectNotificationChannel(channel);
    setLinkedAccounts((prev) => prev.filter((a) => a.channel !== channel));
  };

  const onUpdate = () => initData();

  const StatusBadge = ({ connected }: { connected: boolean }) => (
    <span className={`badge ${connected ? 'badge-success' : 'badge-ghost'}`}>
      {connected ? 'Connected' : 'Not connected'}
    </span>
  );

  const RefreshButton = () => (
    <button className="btn btn-xs btn-outline" onClick={onUpdate}>
      Refresh
    </button>
  );

  const aiToolEntries: IntegrationEntry[] = [
    {
      id: 'mcp',
      icon: <LuServer />,
      iconClassName: 'text-primary',
      title: 'MCP Server',
      description: 'Connect Claude to your mFlow account via the Model Context Protocol.',
      connected: false,
      highlight: true,
      badge: <span className="badge badge-primary badge-outline">New</span>,
      content: <McpIntegration />,
    },
  ];

  const trackerEntries: IntegrationEntry[] = [
    {
      id: 'jira',
      icon: <DiJira />,
      iconClassName: 'text-blue-500',
      title: 'Jira',
      description: 'Sync projects, boards, and tasks, and compute delivery metrics.',
      connected: isJiraConnected,
      status: (
        <div className="flex items-center gap-2">
          <StatusBadge connected={isJiraConnected} />
          <RefreshButton />
        </div>
      ),
      content: <JiraIntegration onUpdate={onUpdate} isConnected={isJiraConnected} onDisconnect={onDisconnect} />,
    },
    {
      id: 'trello',
      icon: <DiTrello />,
      iconClassName: 'text-sky-500',
      title: 'Trello',
      description: 'Sync boards, lists, and cards.',
      connected: isTrelloConnected,
      status: (
        <div className="flex items-center gap-2">
          <StatusBadge connected={isTrelloConnected} />
          <RefreshButton />
        </div>
      ),
      content: <TrelloIntegration onUpdate={onUpdate} isConnected={isTrelloConnected} onDisconnect={onDisconnect} />,
    },
    {
      id: 'github',
      icon: <FaGithub />,
      iconClassName: 'text-base-content',
      title: 'GitHub',
      description: 'Let the coding agent work on an existing repo.',
      connected: isGithubConnected,
      status: (
        <div className="flex items-center gap-2">
          <StatusBadge connected={isGithubConnected} />
          <RefreshButton />
        </div>
      ),
      content: <GithubIntegration onUpdate={onUpdate} isConnected={isGithubConnected} onDisconnect={onDisconnect} />,
    },
  ];

  const aiProviderEntries: IntegrationEntry[] = [
    {
      id: 'openai',
      icon: <SiOpenai />,
      iconClassName: 'text-teal-500',
      title: 'OpenAI',
      description: 'Bring your own key so the coding agent can run on GPT models.',
      connected: !!credentialFor('openai'),
      content: <AiProviderIntegration provider="openai" credential={credentialFor('openai')} onUpdate={onUpdate} />,
    },
    {
      id: 'anthropic',
      icon: <SiAnthropic />,
      iconClassName: 'text-rose-500',
      title: 'Claude',
      description: 'Bring your own key so the coding agent can run on Claude models.',
      connected: !!credentialFor('anthropic'),
      content: (
        <AiProviderIntegration provider="anthropic" credential={credentialFor('anthropic')} onUpdate={onUpdate} />
      ),
    },
    {
      id: 'google',
      icon: <SiGooglegemini />,
      iconClassName: 'text-indigo-500',
      title: 'Gemini',
      description: 'Bring your own key, or use mFlow-hosted Gemini within your monthly limit.',
      connected: !!credentialFor('google'),
      content: <AiProviderIntegration provider="google" credential={credentialFor('google')} onUpdate={onUpdate} />,
    },
    {
      id: 'deepseek',
      // No DeepSeek brand icon in react-icons yet (checked si/index.d.ts) — generic stand-in.
      icon: <LuBrainCircuit />,
      iconClassName: 'text-violet-500',
      title: 'DeepSeek',
      description: 'Bring your own key so the coding agent can run on DeepSeek models.',
      connected: !!credentialFor('deepseek'),
      content: <AiProviderIntegration provider="deepseek" credential={credentialFor('deepseek')} onUpdate={onUpdate} />,
    },
  ];

  const notificationEntries: IntegrationEntry[] = [
    {
      id: 'telegram',
      icon: <FaTelegram />,
      iconClassName: 'text-cyan-500',
      title: 'Telegram',
      description: 'Chat with the MFlow AI assistant directly from Telegram.',
      connected: !!telegramAccount,
      status: <StatusBadge connected={!!telegramAccount} />,
      content: (
        <TelegramIntegration
          isConnected={!!telegramAccount}
          externalId={telegramAccount?.externalId}
          linkedAt={telegramAccount?.linkedAt}
          onRefresh={loadLinkedAccounts}
          onDisconnect={() => onDisconnectNotification('telegram')}
        />
      ),
    },
    {
      id: 'slack',
      icon: <FaSlack />,
      iconClassName: 'text-purple-500',
      title: 'Slack',
      description: 'Receive AI summaries and project notifications in your Slack workspace.',
      connected: !!slackAccount,
      status: <StatusBadge connected={!!slackAccount} />,
      content: (
        <SlackIntegration
          isConnected={!!slackAccount}
          workspaceId={slackAccount?.workspaceId}
          linkedAt={slackAccount?.linkedAt}
          onRefresh={loadLinkedAccounts}
          onDisconnect={() => onDisconnectNotification('slack')}
        />
      ),
    },
    {
      id: 'email',
      icon: <LuMail />,
      iconClassName: 'text-emerald-500',
      title: 'Email',
      description: 'Receive AI summaries and project notifications by email.',
      connected: !!emailAccount,
      status: <StatusBadge connected={!!emailAccount} />,
      content: (
        <EmailIntegration
          isConnected={!!emailAccount}
          externalId={emailAccount?.externalId}
          linkedAt={emailAccount?.linkedAt}
          onRefresh={loadLinkedAccounts}
          onDisconnect={() => onDisconnectNotification('email')}
        />
      ),
    },
  ];

  // MCP has no connect/disconnect state of its own (just setup instructions), so it's shown but
  // deliberately excluded from the connected/total ratio — same as before this redesign. Recomputed
  // every render (cheap, ~9 booleans) rather than memoized, to avoid re-deriving the entry arrays
  // as a dependency list.
  const countableEntries = [...trackerEntries, ...aiProviderEntries, ...notificationEntries];
  const summary = {
    total: countableEntries.length,
    connected: countableEntries.filter((e) => e.connected).length,
  };

  const allEntries = [...aiToolEntries, ...trackerEntries, ...aiProviderEntries, ...notificationEntries];
  const activeEntry = allEntries.find((e) => e.id === activeId) ?? null;

  const CardSkeleton = () => (
    <div className="card bg-base-100 shadow-card">
      <div className="card-body p-4">
        <div className="flex items-center gap-3">
          <div className="skeleton h-10 w-10 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-32" />
            <div className="skeleton h-3 w-56" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTiles = (entries: IntegrationEntry[]) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {entries.map((entry) => (
        <IntegrationTile
          key={entry.id}
          icon={entry.icon}
          iconClassName={entry.iconClassName}
          title={entry.title}
          description={entry.description}
          connected={entry.connected}
          highlight={entry.highlight}
          badge={entry.badge}
          onClick={() => setActiveId(entry.id)}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <div className="text-2xl font-semibold">Integrations</div>
            <div className="text-sm opacity-70 max-w-lg">
              Connect external services to let MFlow read project structure, track delivery metrics, and generate
              summaries.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <progress className="progress progress-primary w-28" value={summary.connected} max={summary.total} />
            <span className="text-sm opacity-70 whitespace-nowrap">
              {summary.connected}/{summary.total} connected
            </span>
          </div>
        </div>

        {/* Privacy note — collapsed by default so it doesn't compete for attention with the
            tile grid below. */}
        <details className="collapse collapse-arrow bg-base-100 shadow-card rounded-xl">
          <summary className="collapse-title text-sm font-medium min-h-0 py-3">Privacy &amp; data handling</summary>
          <div className="collapse-content text-sm opacity-70 space-y-2">
            <p>
              MFlow does not store your files or issue content. We keep only access tokens required to manage
              connections and sync metadata.
            </p>
            <div>
              Used for:
              <ul className="list-disc list-inside mt-1">
                <li>Reading/creating projects/boards/tasks</li>
                <li>Health &amp; delivery stats</li>
                <li>Notifications &amp; summaries</li>
              </ul>
            </div>
          </div>
        </details>

        {/* Errors */}
        {error && (
          <div className="alert alert-error shadow-card">
            <span>{error}</span>
          </div>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-3">
              <SectionLabel>AI tools</SectionLabel>
              {renderTiles(aiToolEntries)}
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <SectionLabel>Project trackers</SectionLabel>
                <div className="flex flex-wrap items-center gap-2">
                  {integrationUrls.jiraUrl && (
                    <a
                      className="btn btn-sm btn-outline"
                      href={integrationUrls.jiraUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <DiJira className="text-base" />
                      Open Jira
                    </a>
                  )}
                  {integrationUrls.trelloUrl && (
                    <a
                      className="btn btn-sm btn-outline"
                      href={integrationUrls.trelloUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <DiTrello className="text-base" />
                      Open Trello
                    </a>
                  )}
                </div>
              </div>
              {renderTiles(trackerEntries)}
            </div>

            <div className="space-y-3">
              <SectionLabel>AI provider keys (BYOK)</SectionLabel>
              {renderTiles(aiProviderEntries)}
            </div>

            <div className="space-y-3">
              <SectionLabel>Notifications</SectionLabel>
              {renderTiles(notificationEntries)}
            </div>
          </div>
        )}

        {activeEntry && (
          <Modal
            onClose={() => setActiveId(null)}
            className="max-w-lg w-full max-h-[85vh] overflow-y-auto"
            labelledBy="integration-modal-title"
          >
            <IntegrationCard
              icon={activeEntry.icon}
              iconClassName={activeEntry.iconClassName}
              title={activeEntry.title}
              description={activeEntry.description}
              status={activeEntry.status}
              onClose={() => setActiveId(null)}
            >
              {activeEntry.content}
            </IntegrationCard>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default IntegrationPage;
